import React, { useState, useEffect, useRef } from "react";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import img1 from "../../Image/Profile.png";
import formatDateTime from "../../components/formatDateTime";
import {
  LuArrowLeft,
  LuArrowRight,
  LuArrowDownAZ,
  LuArrowUpAZ,
  LuChevronDown,
  LuCoins,
  LuDot,
  LuDownload,
  LuFileSpreadsheet,
  LuFileText,
  LuSearch,
  LuTrash2,
  LuUsers,
  LuBan,
  LuCheck,
  LuSquarePen,
  LuX,
  LuSmartphone,
  LuClock,
} from "react-icons/lu";
import generateCaptcha from "../../components/captcha";
import MainButton from "../../components/ul/MainButton";
import NotFound from "../../components/ul/NotFound";
import getISTDate from "../../components/getISTDate";
import toIST from "../../components/toIST";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DATE_RANGES = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["thisWeek", "This Week"],
  ["thisMonth", "This Month"],
  ["last6Months", "Last 6 Months"],
  ["thisYear", "This Year"],
];

const User = () => {
  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOrder, setSortOrder] = useState(-1);
  const [selectAll, setSelectAll] = useState(false);
  const [startDate, setStartDate] = useState(getISTDate());
  const [endDate, setEndDate] = useState(getISTDate());
  const [totalUser, setTotalUser] = useState(0);
  const [blockUser, setBlockUser] = useState(false);
  const [selectState, setSelectState] = useState("today");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const limit = 200;

  // Guards against out-of-order responses. Two fetches can easily be in flight at once (switching
  // range while the previous one is still loading), and they do NOT come back in the order they
  // were sent — a "This Year" query takes far longer than a "Today" one. Without this, picking
  // Today right after This Year showed This Year's rows, because the slow earlier response landed
  // last and overwrote the correct one.
  const requestIdRef = useRef(0);

  // ✅ Fetch only created user data
  const fetchData = async () => {
    const requestId = ++requestIdRef.current;
    setLoader(true);
    try {
      const response = await Calling.GetAllUserData(
        currentPage,
        startDate,
        endDate,
        limit,
        search,
        sortOrder,
        blockUser,
        "",
        "created", // always created filter
      );
      // A newer request has been sent since this one started — its answer is the current truth,
      // so drop this stale one instead of painting it over the table.
      if (requestId !== requestIdRef.current) return;

      if (response?.status) {
        setData(response?.data);
        setTotalPages(response?.totalPage);
        setTotalUser(response?.totalRecord);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      // Only the newest request owns the loader, or a stale one finishing would hide the spinner
      // while the real query is still running.
      if (requestId === requestIdRef.current) setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, sortOrder, startDate, endDate, blockUser]);

  const handleSelectAll = () => {
    if (!selectAll) {
      const allUserIds = data.map((user) => user._id);
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (id) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id],
    );
  };

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = end = toIST(today);
        break;

      case "yesterday":
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        start = end = toIST(y);
        break;

      case "thisWeek":
        const first = new Date(today);
        first.setDate(today.getDate() - today.getDay());
        const last = new Date(first);
        last.setDate(first.getDate() + 6);
        start = toIST(first);
        end = toIST(last);
        break;

      case "thisMonth":
        const sm = new Date(today.getFullYear(), today.getMonth(), 1);
        const em = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(sm);
        end = toIST(em);
        break;

      case "last6Months":
        const s6 = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        const e6 = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        start = toIST(s6);
        end = toIST(e6);
        break;

      case "thisYear":
        const sy = new Date(today.getFullYear(), 0, 1);
        const ey = new Date(today.getFullYear(), 11, 31);
        start = toIST(sy);
        end = toIST(ey);
        break;

      default:
        start = end = "";
    }

    setSelectState(range);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
    // No fetchData() here on purpose. setState is asynchronous, so calling it on this line would
    // run the query with the PREVIOUS range still in the closure — and the effect below then fires
    // a second query with the new range, leaving two in flight for every click. The effect alone
    // is correct, and it is how handleBlockFilter / handleFormDate already work on this page.
  };

  const handleDeleteSelected = async (id) => {
    const check = generateCaptcha(
      "You are about to delete a user. To confirm, please solve this math problem.",
    );
    if (!check) return toast.error("Captcha answer is incorrect");
    try {
      setLoader(true);
      const body = { id: id ? [id] : selectedUsers };
      const response = await Calling.DeleteUser(body);
      if (response?.status) {
        toast.success(response?.message);
        setSelectedUsers([]);
        setSelectAll(false);
        fetchData();
      } else toast.error(response?.message);
    } catch (e) {
      console.error("Error deleting users:", e);
    } finally {
      setLoader(false);
    }
  };

  const handleStartEdit = (user) => {
    setEditingId(user._id);
    setEditForm({
      u_name: user.u_name || "",
      u_email: user.u_email || "",
      u_coin: user.u_coin ?? 0,
      u_device: user.u_device || "",
    });
  };

  const handleSaveEdit = async (id) => {
    if (!editForm.u_name || !editForm.u_email) {
      return toast.error("Name and email are required");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.u_email)) {
      return toast.error("Please provide a valid email");
    }
    setLoader(true);
    try {
      const resp = await Calling.UpdateUserInfo(id, {
        u_name: editForm.u_name,
        u_email: editForm.u_email,
        u_coin: Number(editForm.u_coin) || 0,
        u_device: editForm.u_device || "",
      });
      if (resp?.status) {
        toast.success(resp?.message || "User updated successfully");
        setEditingId(null);
        fetchData();
      } else {
        toast.error(resp?.message || "Failed to update user");
      }
    } catch (e) {
      console.error("Update user error:", e);
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const safeDate = (dt) => {
    try {
      return dt ? formatDateTime(dt) : "N/A";
    } catch {
      return "N/A";
    }
  };

  const handleBlockUser = async (id, status) => {
    setLoader(true);
    try {
      const res = await Calling.BlockUser(id, status);
      res?.status ? toast.success(res?.message) : toast.error(res?.message);
      fetchData();
    } catch (err) {
      console.error("Error blocking user:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleReportSort = () => {
    setSelectState("sort");
    setSortOrder(sortOrder === 1 ? -1 : 1);
  };

  const handleBlockFilter = () => {
    setBlockUser(!blockUser);
    setSelectState("block");
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);
    } else {
      setEndDate(e.target.value);
    }
    setSelectState("range");
  };

  const handleSearchUser = (e) => {
    setCurrentPage(1);
    setSearch(e.target.value);
    setSelectState("search");
  };

  // Debounced search. Runs on an EMPTY box too: guarding on `search.trim() !== ""` meant clearing
  // the box never re-queried, so the table stayed stuck on the last search's rows even though the
  // field was empty. The mount run is skipped instead, which is the only thing that guard was
  // really buying — the main effect above already does the first load.
  const searchMountedRef = useRef(false);
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    const delay = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line
  }, [search]);

  const handleDownloadExcel = async () => {
    const resp = await Calling.GetAllUserExal(startDate, endDate);

    if (!resp?.status) {
      return toast.error(resp.message || "No data found");
    }

    const users = resp.data;

    if (!users?.length) {
      return toast.error("No users available for this date range");
    }

    // Prepare Excel rows
    const excelRows = users.map((u, i) => ({
      No: i + 1,
      Name: u.u_name || "",
      Email: u.u_email || "",
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelRows);
    XLSX.utils.book_append_sheet(wb, ws, "Users");

    // Write file
    XLSX.writeFile(wb, `Users_${new Date().toISOString()}.xlsx`);
  };

  const handleDownloadPDF = async () => {
    try {
      // 1️⃣ CALL YOUR API
      const resp = await Calling?.GetAllUserExal(startDate, endDate);

      if (!resp?.status) {
        return toast.error(resp?.message || "No data found");
      }

      const users = resp?.data || [];

      if (users.length === 0) {
        return toast.error("No users found in this date range");
      }

      // 2️⃣ PREPARE PDF ROWS
      const pdfRows = users.map((u, i) => [
        i + 1,
        u.u_name || "",
        u.u_email || "",
      ]);

      // 3️⃣ CREATE PDF
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text("User Report", 14, 15);

      autoTable(doc, {
        head: [["No", "Name", "Email"]],
        body: pdfRows,
        startY: 25,
      });

      // 4️⃣ DOWNLOAD PDF
      doc.save(`Users_${new Date().toISOString()}.pdf`);
    } catch (error) {
      console.log("PDF download error:", error);
      toast.error("Something went wrong");
    }
  };

  const dateInputCls = (active) =>
    `ui-input cursor-pointer rounded-xl px-3 py-2 text-sm ${
      active ? "border-teal-400 text-brand-teal" : ""
    }`;

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Header + date range */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 pt-6 pb-5">
        <div className="flex items-center gap-4">
          <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
            <LuUsers className="text-2xl md:text-3xl" />
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
              User Management
            </h1>
            <p className="text-sm text-main-subfont mt-1.5">
              Manage and monitor all registered users
            </p>
          </div>
        </div>

        {/* Date inputs (desktop) */}
        <form className="hidden md:flex items-center gap-2">
          <input
            type="date"
            name="startDate"
            value={startDate}
            onChange={handleFormDate}
            className={dateInputCls(selectState === "range")}
          />
          <span className="text-main-subfont text-sm">to</span>
          <input
            type="date"
            name="endDate"
            value={endDate}
            onChange={handleFormDate}
            className={dateInputCls(selectState === "range")}
          />
        </form>
      </div>

      {/* Date range pills (slim toolbar) */}
      <div className="flex flex-wrap items-center gap-2 pb-5 border-b border-main-border">
        {/* Mobile dropdown */}
        <div className="sm:hidden w-full">
          <select
            className="ui-input w-full px-3 py-2 rounded-xl text-sm cursor-pointer"
            onChange={(e) => handleDateRangeFilter(e.target.value)}
            value={selectState}
          >
            {DATE_RANGES.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
            <option value="range">Custom Range</option>
          </select>
        </div>
        {/* Desktop pills */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {DATE_RANGES.map(([key, label]) => (
            <MainButton
              key={key}
              label={label}
              selectState={selectState === key}
              onClick={() => handleDateRangeFilter(key)}
            />
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mt-5">
        <div className="flex flex-wrap items-center gap-2">
          <MainButton
            onClick={handleBlockFilter}
            selectState={selectState === "block"}
            label={blockUser ? "Unblock" : "Block"}
          />
          <MainButton
            onClick={handleReportSort}
            selectState={selectState === "sort"}
            label={
              <div className="flex gap-2 items-center">
                {sortOrder === -1 ? <LuArrowDownAZ /> : <LuArrowUpAZ />}
                <span>Sort</span>
              </div>
            }
          />
          <DownloadDropdown
            onDownloadExcel={handleDownloadExcel}
            onDownloadPDF={handleDownloadPDF}
          />
          {selectedUsers.length > 0 && (
            <button
              onClick={() => handleDeleteSelected(null)}
              className="inline-flex items-center gap-2 rounded-xl bg-red-500/90 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 transition-all"
            >
              <LuTrash2 size={16} /> Delete ({selectedUsers.length})
            </button>
          )}
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-main-border px-4 py-2.5 text-sm text-main-subfont">
            <LuUsers size={16} className="text-brand-teal" /> Total
            <b className="text-main-font">
              {totalUser < 10 ? "0" + totalUser : totalUser}
            </b>
          </span>
          <label className="inline-flex items-center gap-2 rounded-xl bg-white border border-main-border px-4 py-2.5 text-sm text-main-subfont cursor-pointer">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              className="accent-teal-500 w-4 h-4"
            />
            Select all
          </label>
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-[300px]">
          <LuSearch
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-main-subfont z-10"
            size={18}
          />
          <input
            type="search"
            placeholder="Search name or email"
            value={search}
            onChange={handleSearchUser}
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-main-border text-main-font placeholder-main-subfont outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
        </div>
      </div>

      {/* User cards */}
      {loader ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white border border-main-border shadow-sm p-5 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-black/10" />
                <div className="flex-1">
                  <div className="w-28 h-3.5 bg-black/10 rounded mb-2" />
                  <div className="w-40 h-3 bg-black/5 rounded" />
                </div>
              </div>
              <div className="flex justify-between mt-5">
                <div className="w-16 h-5 bg-black/10 rounded-full" />
                <div className="w-16 h-5 bg-black/10 rounded-full" />
              </div>
              <div className="flex gap-2 mt-5">
                <div className="flex-1 h-9 bg-black/10 rounded-lg" />
                <div className="w-9 h-9 bg-black/10 rounded-lg" />
                <div className="w-9 h-9 bg-black/10 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : data.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {data.map((user) => (
            <div
              key={user._id}
              className={`rounded-2xl bg-white border p-5 shadow-sm transition-all duration-300 ${
                selectedUsers.includes(user._id)
                  ? "border-teal-400/50 ring-1 ring-brand-teal/30"
                  : "border-main-border hover:border-brand-teal/40 hover:shadow-md"
              }`}
            >
              {/* Identity + select */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={user?.u_profile || img1}
                    alt={user?.u_name || "user"}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = img1;
                    }}
                    className="w-12 h-12 rounded-full ring-1 ring-main-border object-cover shrink-0 bg-main-bg"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-main-font truncate">
                      {user.u_name || "N/A"}
                    </p>
                    <p className="text-xs text-main-subfont truncate">
                      {user.u_email || "N/A"}
                    </p>
                    {user.u_status ? (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 text-[11px] rounded-full bg-teal-500/15 text-brand-teal gap-1 font-medium">
                        <LuDot size={12} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center mt-1 px-2 py-0.5 text-[11px] rounded-full bg-red-500/15 text-red-600 gap-1 font-medium">
                        <LuDot size={12} /> Blocked
                      </span>
                    )}
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user._id)}
                  onChange={() => handleSelectUser(user._id)}
                  className="accent-teal-500 w-4 h-4 cursor-pointer mt-1 shrink-0"
                />
              </div>

              {editingId === user._id ? (
                /* Inline edit form */
                <>
                  <div className="mt-4 pt-4 border-t border-main-border grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Name
                      </label>
                      <input
                        value={editForm.u_name}
                        onChange={(e) =>
                          setEditForm({ ...editForm, u_name: e.target.value })
                        }
                        className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.u_email}
                        onChange={(e) =>
                          setEditForm({ ...editForm, u_email: e.target.value })
                        }
                        className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Coin
                      </label>
                      <input
                        type="number"
                        value={editForm.u_coin}
                        onChange={(e) =>
                          setEditForm({ ...editForm, u_coin: e.target.value })
                        }
                        className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Device ID
                      </label>
                      <input
                        value={editForm.u_device}
                        onChange={(e) =>
                          setEditForm({ ...editForm, u_device: e.target.value })
                        }
                        className="ui-input w-full rounded-lg px-3 py-2 text-sm mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleSaveEdit(user._id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
                    >
                      <LuCheck size={16} /> Save
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-main-border bg-white px-3 py-2 text-sm font-medium text-main-subfont hover:text-main-font hover:border-brand-teal/40 transition-all"
                    >
                      <LuX size={16} /> Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* All details */}
                  <div className="mt-4 pt-4 border-t border-main-border grid grid-cols-2 gap-x-3 gap-y-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Coin
                      </p>
                      <p className="text-sm font-semibold text-main-font mt-0.5 inline-flex items-center gap-1.5">
                        <LuCoins size={14} className="text-brand-teal" />
                        {user.u_coin ?? 0}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Device ID
                      </p>
                      <p className="text-sm font-semibold text-main-font mt-0.5 truncate inline-flex items-center gap-1.5 max-w-full">
                        <LuSmartphone size={14} className="text-brand-teal shrink-0" />
                        <span className="truncate">{user.u_device || "N/A"}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Last Login
                      </p>
                      <p className="text-xs font-medium text-main-font mt-0.5 inline-flex items-center gap-1.5">
                        <LuClock size={13} className="text-brand-teal" />
                        {safeDate(user.last_login)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-main-subfont">
                        Joined
                      </p>
                      <p className="text-xs font-medium text-main-font mt-0.5">
                        {safeDate(user.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleStartEdit(user)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 px-3 py-2 text-sm font-semibold text-white shadow-md shadow-teal-500/25 hover:brightness-110 transition-all"
                    >
                      <LuSquarePen size={16} /> Edit
                    </button>
                    <button
                      onClick={() => handleBlockUser(user._id, !user.u_status)}
                      title={user.u_status ? "Block user" : "Unblock user"}
                      className="inline-flex items-center justify-center rounded-lg border border-main-border bg-white px-3 py-2 text-sm font-medium text-main-subfont hover:text-main-font hover:border-brand-teal/40 transition-all"
                    >
                      {user.u_status ? <LuBan size={16} /> : <LuCheck size={16} />}
                    </button>
                    <button
                      onClick={() => handleDeleteSelected(user._id)}
                      title="Delete user"
                      className="inline-flex items-center justify-center rounded-lg bg-red-500/90 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 transition-all"
                    >
                      <LuTrash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8">
          <NotFound title="No users found" />
        </div>
      )}

      {/* Pagination */}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 text-sm font-medium rounded-xl flex items-center gap-2 transition-all ${
                currentPage === 1
                  ? "bg-white text-main-subfont cursor-not-allowed border border-main-border"
                  : "bg-white border border-main-border text-main-font hover:border-teal-400/50 hover:text-brand-teal"
              }`}
            >
              <LuArrowLeft size={14} /> Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 text-sm font-medium rounded-xl flex items-center gap-2 transition-all ${
                currentPage === totalPages
                  ? "bg-white text-main-subfont cursor-not-allowed border border-main-border"
                  : "bg-white border border-main-border text-main-font hover:border-teal-400/50 hover:text-brand-teal"
              }`}
            >
              Next <LuArrowRight size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-main-subfont">
            Page
            <span className="px-3 py-1.5 bg-white border border-main-border rounded-lg font-semibold text-main-font">
              {currentPage <= 9 ? `0${currentPage}` : currentPage}
            </span>
            of
            <span className="px-3 py-1.5 bg-white border border-main-border rounded-lg font-semibold text-main-font">
              {totalPages <= 9 ? `0${totalPages}` : totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default User;

const DownloadDropdown = ({ onDownloadExcel, onDownloadPDF }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownload = (type) => {
    if (type === "excel") onDownloadExcel();
    else if (type === "pdf") onDownloadPDF();
    setShowDropdown(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl hover:brightness-110 transition-all shadow-lg shadow-teal-500/30 font-medium text-sm"
      >
        <LuDownload size={16} />
        <span>Download</span>
        <LuChevronDown
          size={16}
          className={`transition-transform ${showDropdown ? "rotate-180" : ""}`}
        />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-48 bg-white backdrop-blur border border-main-border rounded-xl shadow-xl z-50 overflow-hidden">
          <button
            onClick={() => handleDownload("excel")}
            className="flex items-center gap-3 px-4 py-3 text-main-font hover:bg-main-bg w-full text-left transition-colors"
          >
            <LuFileSpreadsheet size={18} className="text-brand-green" />
            <span className="font-medium">Download Excel</span>
          </button>
          <div className="border-t border-main-border"></div>
          <button
            onClick={() => handleDownload("pdf")}
            className="flex items-center gap-3 px-4 py-3 text-main-font hover:bg-red-500/15 w-full text-left transition-colors"
          >
            <LuFileText size={18} className="text-red-600" />
            <span className="font-medium">Download PDF</span>
          </button>
        </div>
      )}
    </div>
  );
};
